import { CargoFieldName, IssueSeverity } from '../frontend/src/api/generated-types';
import type { AdditionalRequirements } from '../frontend/src/api/types';

// Rule-tree grammar. Pure TS types — no runtime parser.
//
// The spec lives in `tree.ts` next to this file; BE walks the serialized JSON
// (see `RuleInterpreter.cs`). FE consumes these types for its visibility
// evaluator (`frontend/src/utils/validation/ruleTree/visibility.ts`).
//
// CtxFlagId, VisibilityPredicateId, RulePredicateId, GetterPredicateId are
// hand-mirrored literal unions; drift against FE runtime is caught by
// `satisfies` constraints in `visibility.ts` and `ruleContext.ts`.

export type DgErrorKey = `${CargoFieldName}`;
export type RuleFailure = { message: string };
export type RequiresKey = keyof AdditionalRequirements;

export type CtxFlagId =
  | 'isCoolantOrConditioner'
  | 'isEmptyUncleaned'
  | 'isStabilized'
  | 'isStabilizedNotByTC'
  | 'isMarinePollutant'
  | 'isTankUnit'
  | 'hasRequirements';

export type VisibilityPredicateId = 'multipleMatchesVisible' | 't75HoldingTime';
export type RulePredicateId = 'validateSegregationGroups';
export type GetterPredicateId = 'substanceIdValue';

export type CompareOp = 'gte' | 'gt' | 'lte' | 'lt' | 'eq';

export type Visibility =
  | { kind: 'requires'; requires: RequiresKey }
  | { kind: 'ctxFlag'; ctxFlag: CtxFlagId }
  | { kind: 'named'; named: VisibilityPredicateId }
  | { kind: 'not'; not: Visibility }
  | { kind: 'all'; all: Visibility[] }
  | { kind: 'any'; any: Visibility[] }
  | { kind: 'allBlank'; allBlank: string[] };

export type ValuePath = string | { named: GetterPredicateId } | { orFallback: string[] };

export type Rule =
  | { kind: 'nonBlank'; message: string }
  | { kind: 'positiveNumber'; message: string }
  | { kind: 'regex'; pattern: string; message: string }
  | { kind: 'compare'; op: CompareOp; otherPath: string; message: string }
  | { kind: 'when'; condition: Visibility; rule: Rule }
  | { kind: 'named'; id: RulePredicateId; message?: string };

type BaseFieldNode<S> = {
  kind: 'field';
  errorKey: DgErrorKey;
  section?: S;
  label?: string;
  hash?: boolean;
  visible: Visibility;
  valuePath: ValuePath;
  severityLocked?: boolean;
};

export type SharedFieldNode<S = string> = BaseFieldNode<S> & {
  defaultSeverity?: IssueSeverity;
  rules: Rule[];
  owner?: never;
};

// FE renders + hashes the field but skips rule walks; BE-only handlers in
// `CargoValidator` emit the issues. Set-equality with `BeOwnedHandled` is
// asserted at BE boot.
export type BeOwnedFieldNode<S = string> = BaseFieldNode<S> & {
  owner: 'be';
};

export type SchemaFieldNode<S = string> = SharedFieldNode<S> | BeOwnedFieldNode<S>;

export type SchemaGroupNode<S = string> = {
  kind: 'group';
  visible: Visibility;
  children: Record<string, SchemaFieldNode<S> | SchemaGroupNode<S>>;
};

export type RuleSchema<S = string> = Record<string, SchemaFieldNode<S> | SchemaGroupNode<S>>;

export type ValidationError = {
  errorKey: DgErrorKey;
  message: string;
  defaultSeverity?: IssueSeverity;
};

export function isFieldNode<S>(
  node: SchemaFieldNode<S> | SchemaGroupNode<S>
): node is SchemaFieldNode<S> {
  return node.kind === 'field';
}

export function isGroupNode<S>(
  node: SchemaFieldNode<S> | SchemaGroupNode<S>
): node is SchemaGroupNode<S> {
  return node.kind === 'group';
}

export function isBeOwnedField<S>(node: SchemaFieldNode<S>): node is BeOwnedFieldNode<S> {
  return 'owner' in node && node.owner === 'be';
}
